import React, { useRef, useEffect } from 'react';
import { X, Info } from 'lucide-react';

const EntityDetailsWindow = ({
  isVisible,
  selectedEntity,
  graphData,
  floatingWindow,
  setFloatingWindow,
  onClose
}) => {
  const floatingRef = useRef(null);

  // Floating window handlers
  const handleFloatingMouseDown = (e) => {
    if (e.target.classList.contains('resize-handle')) {
      setFloatingWindow(prev => ({ ...prev, isResizing: true }));
    } else if (e.target.classList.contains('drag-handle') || e.target.closest('.drag-handle')) {
      const rect = floatingRef.current?.getBoundingClientRect();
      if (rect) {
        setFloatingWindow(prev => ({
          ...prev,
          isDragging: true,
          dragOffset: {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
          }
        }));
      }
    }
  };

  const handleFloatingMouseMove = (e) => {
    if (floatingWindow.isDragging) {
      setFloatingWindow(prev => ({
        ...prev,
        position: {
          x: e.clientX - prev.dragOffset.x,
          y: e.clientY - prev.dragOffset.y
        }
      }));
    } else if (floatingWindow.isResizing) {
      const rect = floatingRef.current?.getBoundingClientRect();
      if (rect) {
        setFloatingWindow(prev => ({
          ...prev,
          size: {
            width: Math.max(250, e.clientX - rect.left + 10),
            height: Math.max(200, e.clientY - rect.top + 10)
          }
        }));
      }
    }
  };

  const handleFloatingMouseUp = () => {
    setFloatingWindow(prev => ({
      ...prev,
      isDragging: false,
      isResizing: false
    }));
  };

  useEffect(() => {
    if (floatingWindow.isDragging || floatingWindow.isResizing) {
      document.addEventListener('mousemove', handleFloatingMouseMove);
      document.addEventListener('mouseup', handleFloatingMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleFloatingMouseMove);
        document.removeEventListener('mouseup', handleFloatingMouseUp);
      };
    }
  }, [floatingWindow.isDragging, floatingWindow.isResizing]);

  if (!isVisible || !selectedEntity) return null;

  const allEntities = [...(graphData.nodes || [])];
  const entityRelationships = graphData.links.filter(link => 
    link.source === selectedEntity.id || link.target === selectedEntity.id
  );

  const getEntityTypeColor = (type) => {
    switch (type) {
      case 'protagonist': return 'bg-green-100 text-green-800';
      case 'helper': return 'bg-blue-100 text-blue-800';
      case 'sacrificial': return 'bg-red-100 text-red-800';
      case 'community': return 'bg-purple-100 text-purple-800';
      case 'origin': return 'bg-green-100 text-green-800';
      case 'destination': return 'bg-yellow-100 text-yellow-800';
      case 'habitat': return 'bg-teal-100 text-teal-800';
      case 'obstacle': return 'bg-gray-100 text-gray-800';
      case 'dwelling': return 'bg-cyan-100 text-cyan-800';
      case 'setting': return 'bg-emerald-100 text-emerald-800';
      case 'life_force': return 'bg-yellow-100 text-yellow-800';
      case 'essence': return 'bg-purple-100 text-purple-800';
      case 'legacy': return 'bg-red-100 text-red-800';
      case 'artifact': return 'bg-amber-100 text-amber-800';
      case 'symbol': return 'bg-teal-100 text-teal-800';
      case 'bond': return 'bg-sky-100 text-sky-800';
      case 'theme': return 'bg-pink-100 text-pink-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div
      ref={floatingRef}
      className="fixed bg-white rounded-lg shadow-2xl border border-gray-300 z-50"
      style={{
        left: floatingWindow.position.x,
        top: floatingWindow.position.y,
        width: floatingWindow.size.width,
        height: floatingWindow.size.height,
        minWidth: '250px',
        minHeight: '200px'
      }}
      onMouseDown={handleFloatingMouseDown}
    >
      {/* Header with drag handle */}
      <div className="drag-handle flex items-center justify-between p-3 bg-red-600 text-white rounded-t-lg cursor-move">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4" />
          <h4 className="font-semibold text-sm">實體詳情</h4>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onClose}
            className="p-1 hover:bg-red-700 rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 h-full overflow-auto" style={{ height: 'calc(100% - 48px)' }}>
        <div className="space-y-4">
          {/* Entity Basic Info */}
          <div className="border-b border-gray-200 pb-3">
            <h5 className="font-bold text-lg text-gray-900 mb-2">{selectedEntity.name}</h5>
            <p className="text-sm text-gray-600 leading-relaxed">{selectedEntity.description}</p>
          </div>
          
          {/* Entity Type and Importance */}
          <div className="flex items-center justify-between">
            <span className={`inline-block px-3 py-1 text-xs rounded-full font-medium ${getEntityTypeColor(selectedEntity.type)}`}>
              {selectedEntity.type}
            </span>
            <span className="text-sm text-gray-500 font-medium">
              重要性分數: {selectedEntity.importance}/10
            </span>
          </div>

          {/* Entity Statistics */}
          <div className="bg-gray-50 rounded-lg p-3">
            <h6 className="font-semibold text-gray-900 mb-2 text-sm">實體統計</h6>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="text-center p-2 bg-white rounded border">
                <div className="text-lg font-bold text-blue-600">{entityRelationships.length}</div>
                <div className="text-gray-600">關係數量</div>
              </div>
              <div className="text-center p-2 bg-white rounded border">
                <div className="text-lg font-bold text-green-600">{selectedEntity.importance}</div>
                <div className="text-gray-600">重要性</div>
              </div>
            </div>
          </div>

          {/* Relationships Section */}
          <div>
            <h6 className="font-semibold text-gray-900 mb-3 text-sm flex items-center gap-2">
              <span>關係詳情</span>
              <span className="text-xs text-gray-500">({entityRelationships.length})</span>
            </h6>
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {entityRelationships.length > 0 ? (
                entityRelationships.map((relation, index) => {
                  const relatedId = relation.source === selectedEntity.id ? relation.target : relation.source;
                  const relatedEntity = allEntities.find(e => e.id === relatedId);
                  const isOutgoing = relation.source === selectedEntity.id;

                  if (!relatedEntity) return null;

                  return (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-blue-600">
                            {isOutgoing ? selectedEntity.name : relatedEntity.name}
                          </span>
                          <span className="text-gray-400">→</span>
                          <span className="text-gray-500 italic text-xs">{relation.type}</span>
                          <span className="text-gray-400">→</span>
                          <span className="font-medium text-green-600">
                            {isOutgoing ? relatedEntity.name : selectedEntity.name}
                          </span>
                        </div>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          強度: {relation.strength}/10
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 leading-relaxed">
                        {relatedEntity.description}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center text-gray-500 py-4">
                  <p className="text-sm">此實體暫無關係</p>
                </div>
              )}
            </div>
          </div>

          {/* Additional Entity Information */}
          <div className="border-t border-gray-200 pt-3">
            <h6 className="font-semibold text-gray-900 mb-2 text-sm">實體屬性</h6>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-600">實體ID:</span>
                <span className="font-mono text-gray-800">{selectedEntity.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">類型:</span>
                <span className="text-gray-800">{selectedEntity.type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">重要性:</span>
                <span className="text-gray-800">{selectedEntity.importance}/10</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Resize handle */}
      <div className="resize-handle absolute bottom-0 right-0 w-3 h-3 cursor-se-resize">
        <div className="absolute bottom-1 right-1 w-2 h-2 border-r-2 border-b-2 border-gray-400"></div>
      </div>
    </div>
  );
};

export default EntityDetailsWindow; 